/**
 * Browser-only JSON store for Master Tour Calendar v2 worksheets
 * (settlement, expenses, merch counts). Empty until the user types or imports.
 * Keys are scoped by the signed-in uid so people sharing a browser never read each other's sheets.
 */
import { getAuth } from 'firebase/auth';

function canUse() {
  try {
    return typeof localStorage !== 'undefined' && localStorage;
  } catch {
    return false;
  }
}

function currentUid() {
  try {
    return getAuth().currentUser?.uid || '';
  } catch {
    return '';
  }
}

/** Key prefix for one namespace and user, e.g. `vrc.mtc.merch.@uid.`. '@' never appears in a tour id. */
export function scopedStorePrefix(ns, uid = currentUid()) {
  return `vrc.mtc.${ns}.@${uid || 'anon'}.`;
}

/** The pre-scoping key. Still read once so sheets saved before uid scoping are not lost. */
export function legacyStoreKey(ns, tourId, dateKey) {
  return `vrc.mtc.${ns}.${String(tourId || 'none')}.${String(dateKey || 'none')}`;
}

export function storeKey(ns, tourId, dateKey) {
  return `${scopedStorePrefix(ns)}${String(tourId || 'none')}.${String(dateKey || 'none')}`;
}

/**
 * Raw text for this user's key, falling back to the old un-scoped key.
 * A signed-in user claims the old key (copied into their scope, then removed);
 * without a uid the old key is read but left alone.
 */
function readRaw(ns, tourId, dateKey) {
  const scoped = localStorage.getItem(storeKey(ns, tourId, dateKey));
  if (scoped != null && scoped !== '') return scoped;
  const legacyKey = legacyStoreKey(ns, tourId, dateKey);
  const legacy = localStorage.getItem(legacyKey);
  if (legacy == null || legacy === '') return scoped;
  if (currentUid()) {
    try {
      localStorage.setItem(storeKey(ns, tourId, dateKey), legacy);
      localStorage.removeItem(legacyKey);
    } catch {
      /* quota / private mode: still return the old data */
    }
  }
  return legacy;
}

export function loadStoredJson(ns, tourId, dateKey, fallback) {
  if (!canUse()) return fallback;
  try {
    const raw = readRaw(ns, tourId, dateKey);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

/** Returns null when the key was never written (so a Firestore seed can hydrate). */
export function loadStoredJsonIfPresent(ns, tourId, dateKey) {
  if (!canUse()) return null;
  try {
    const raw = readRaw(ns, tourId, dateKey);
    if (raw == null || raw === '') return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function saveStoredJson(ns, tourId, dateKey, value) {
  if (!canUse()) return;
  try {
    localStorage.setItem(storeKey(ns, tourId, dateKey), JSON.stringify(value));
  } catch {
    /* quota / private mode */
  }
}

export function clearStoredJson(ns, tourId, dateKey) {
  if (!canUse()) return;
  try {
    localStorage.removeItem(storeKey(ns, tourId, dateKey));
    localStorage.removeItem(legacyStoreKey(ns, tourId, dateKey));
  } catch {
    /* ignore */
  }
}
